import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './axiosBaseQuery';
import { onboardingApi } from './onboardingApi';
import {
  GetPassResponse,
  ListPassesResponse,
  CreatePassResponse,
  CreatePassRequest,
  UpdatePassRequest,
  PassListFilterValues,
} from '../../types/pass';

const buildQuery = (
  params: Record<string, string | number | boolean | string[] | undefined>
) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '') {
      if (Array.isArray(v)) {
        v.forEach((item) => qs.append(`${k}[]`, item));
      } else {
        qs.append(k, String(v));
      }
    }
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
};

const SPARSE_FIELDS =
  'extId,passTemplateName,createdAt,deviceType,status,addedAt,passType,passContent,downloadUrl,formSlug,formQueryParams,expiresAt';

const computeAddedAfter = (addedToWalletDate?: string): string | undefined => {
  if (addedToWalletDate === 'today') {
    return new Date(new Date().setHours(0, 0, 0, 0)).toISOString();
  }
  if (addedToWalletDate === '7days') {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString();
  }
  if (addedToWalletDate === '30days') {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString();
  }
  return undefined;
};

export const passApi = createApi({
  reducerPath: 'passApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Passes', 'Pass', 'GroupTags'],
  endpoints: (builder) => ({
    listGroupTags: builder.query<
      { data: { id: string; attributes?: { count?: number } }[] },
      { entityId?: string; tag?: string }
    >({
      query: ({ entityId, tag }) => {
        const base = entityId
          ? `api/v1/entities/${entityId}/group-tags`
          : 'api/v1/group-tags';
        const qs = tag ? buildQuery({ tag }) : '';
        return `${base}${qs}`;
      },
      providesTags: [{ type: 'GroupTags', id: 'LIST' }],
    }),

    getPass: builder.query<GetPassResponse, { uuid: string }>({
      query: ({ uuid }) => `passes/${uuid}`,
      providesTags: (_result, _error, { uuid }) => [{ type: 'Pass', id: uuid }],
    }),

    listPasses: builder.query<
      ListPassesResponse,
      {
        entityId?: string;
        page?: number;
        perPage?: number;
        filters?: Partial<PassListFilterValues>;
        sortBy?: string;
        sortOrder?: string;
      }
    >({
      query: ({ entityId, page, perPage, filters = {}, sortBy, sortOrder }) => {
        const base = entityId
          ? `api/v1/entities/${entityId}/passes`
          : 'api/v1/passes';

        const addedAfter = computeAddedAfter(filters.addedToWalletDate);

        const qs = buildQuery({
          fields: SPARSE_FIELDS,
          page,
          perPage,
          uuid: filters.id,
          externalId: filters.extId,
          extIdPrefix: filters.extIdPrefix,
          passTemplateUuid: filters.passTemplate?.length
            ? filters.passTemplate
            : undefined,
          groupTags: filters.groupTag?.length ? filters.groupTag : undefined,
          passType: filters.passType?.length ? filters.passType : undefined,
          deviceType: filters.deviceType,
          status: filters.status?.length ? filters.status : undefined,
          search: filters.search,
          addedAfter,
          sortBy: sortBy || undefined,
          sortOrder: sortOrder || undefined,
        });

        return `${base}${qs}`;
      },
      providesTags: (result) =>
        result
          ? [
              ...(result.data?.map(({ id }) => ({
                type: 'Passes' as const,
                id,
              })) ?? []),
              { type: 'Passes', id: 'LIST' },
            ]
          : [{ type: 'Passes', id: 'LIST' }],
    }),

    createPass: builder.mutation<
      CreatePassResponse,
      {
        passTemplateUuid: string;
        extId?: string;
        data: CreatePassRequest;
      }
    >({
      query: ({ passTemplateUuid, extId, data }) => {
        const qs = buildQuery({
          pass_template: passTemplateUuid,
          ext_id: extId,
        });
        return {
          url: `create-passes${qs}`,
          method: 'POST',
          data,
        };
      },
      invalidatesTags: [{ type: 'Passes', id: 'LIST' }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          // Onboarding step 2 is "first pass created" — invalidate to fire the completion modal.
          await queryFulfilled;
          dispatch(onboardingApi.util.invalidateTags(['Onboarding']));
        } catch {
          // Mutation failed — nothing to invalidate.
        }
      },
    }),

    updatePass: builder.mutation<
      void,
      { uuid: string; data: UpdatePassRequest }
    >({
      query: ({ uuid, data }) => ({
        url: `passes/${uuid}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (_result, error, { uuid }) =>
        error
          ? []
          : [
              { type: 'Pass', id: uuid },
              { type: 'Passes', id: 'LIST' },
              { type: 'GroupTags', id: 'LIST' },
            ],
    }),

    deactivatePass: builder.mutation<void, { uuid: string }>({
      query: ({ uuid }) => ({
        url: `passes/${uuid}`,
        method: 'PATCH',
        data: {
          pass: {
            nfc: { enabled: false, source: 'uuid' },
            barcode: {
              enabled: false,
              type: null,
              source: null,
              value: '',
              displayText: false,
            },
          },
          passAction: 'deactivate',
        },
      }),
      invalidatesTags: (_result, _error, { uuid }) => [
        { type: 'Pass', id: uuid },
        { type: 'Passes', id: 'LIST' },
      ],
    }),

    sendNotification: builder.mutation<void, { uuid: string; message: string }>(
      {
        query: ({ uuid, message }) => ({
          url: `api/v1/passes/${uuid}/notification`,
          method: 'POST',
          data: {
            notification: { message },
          },
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            // Final onboarding step — fire the go-live modal.
            dispatch(onboardingApi.util.invalidateTags(['Onboarding']));
          } catch {
            // Mutation failed — nothing to invalidate.
          }
        },
      }
    ),

    updateLoyaltyBalance: builder.mutation<
      { data: { data: { id: string; attributes: Record<string, unknown> } }; meta: { loyalty: { balance: number } } },
      { passId: string; balance?: number; overrideBalance?: number }
    >({
      query: ({ passId, balance, overrideBalance }) => ({
        url: `api/v1/loyalty/${passId}`,
        method: 'PATCH',
        data: {
          ...(balance !== undefined && { balance }),
          ...(overrideBalance !== undefined && { overrideBalance }),
        },
      }),
      invalidatesTags: (_result, _error, { passId }) => [
        { type: 'Pass', id: passId },
      ],
    }),

    exportPasses: builder.mutation<
      void,
      {
        entityId?: string;
        email: string;
        timezone: string;
        filters?: Partial<PassListFilterValues>;
      }
    >({
      query: ({ entityId, email, timezone, filters = {} }) => {
        const base = entityId
          ? `api/v1/entities/${entityId}/passes`
          : 'api/v1/passes';

        const qs = buildQuery({
          export: 'true',
          uuid: filters.id,
          externalId: filters.extId,
          extIdPrefix: filters.extIdPrefix,
          passTemplateUuid: filters.passTemplate?.length
            ? filters.passTemplate
            : undefined,
          groupTags: filters.groupTag?.length ? filters.groupTag : undefined,
          passType: filters.passType?.length ? filters.passType : undefined,
          deviceType: filters.deviceType,
          status: filters.status?.length ? filters.status : undefined,
          timezone,
          email,
        });

        return `${base}${qs}`;
      },
    }),
  }),
});

export const {
  useGetPassQuery,
  useLazyGetPassQuery,
  useListPassesQuery,
  useLazyListPassesQuery,
  useListGroupTagsQuery,
  useLazyListGroupTagsQuery,
  useCreatePassMutation,
  useUpdatePassMutation,
  useDeactivatePassMutation,
  useSendNotificationMutation,
  useUpdateLoyaltyBalanceMutation,
  useExportPassesMutation,
} = passApi;
